/**
 * Languages the interview can run in. Every one of these is supported by
 * Sarvam for both speech-to-text and text-to-speech.
 */
export const LANGUAGES = {
  'en-IN': { label: 'English', native: 'English' },
  'hi-IN': { label: 'Hindi', native: 'हिन्दी' },
  'ml-IN': { label: 'Malayalam', native: 'മലയാളം' },
  'ta-IN': { label: 'Tamil', native: 'தமிழ்' },
  'kn-IN': { label: 'Kannada', native: 'ಕನ್ನಡ' },
  'te-IN': { label: 'Telugu', native: 'తెలుగు' },
  'mr-IN': { label: 'Marathi', native: 'मराठी' },
  'bn-IN': { label: 'Bengali', native: 'বাংলা' },
  'gu-IN': { label: 'Gujarati', native: 'ગુજરાતી' },
  'pa-IN': { label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  'od-IN': { label: 'Odia', native: 'ଓଡ଼ିଆ' },
} as const;

export type LanguageCode = keyof typeof LANGUAGES;

export const LANGUAGE_CODES = Object.keys(LANGUAGES) as LanguageCode[];

export function isLanguageCode(value: unknown): value is LanguageCode {
  return typeof value === 'string' && value in LANGUAGES;
}

// Unicode blocks of the Indic scripts, mapped to the language a TTS voice
// should read them in. Devanagari is shared by Hindi and Marathi and Bengali
// script by Bengali and Assamese, so the conversation's own language breaks
// the tie when it uses the same script.
const SCRIPT_RANGES: Array<[number, number, LanguageCode]> = [
  [0x0900, 0x097f, 'hi-IN'],
  [0x0980, 0x09ff, 'bn-IN'],
  [0x0a00, 0x0a7f, 'pa-IN'],
  [0x0a80, 0x0aff, 'gu-IN'],
  [0x0b00, 0x0b7f, 'od-IN'],
  [0x0b80, 0x0bff, 'ta-IN'],
  [0x0c00, 0x0c7f, 'te-IN'],
  [0x0c80, 0x0cff, 'kn-IN'],
  [0x0d00, 0x0d7f, 'ml-IN'],
];

const SAME_SCRIPT: Partial<Record<LanguageCode, LanguageCode>> = { 'mr-IN': 'hi-IN' };

/**
 * Pick the language a piece of text should be spoken in, from its script.
 * Deterministic: the model is told to write Indic languages in their native
 * script, so the script is the most reliable signal of what it meant.
 */
export function detectSpeechLanguage(text: string, conversationLang?: string): LanguageCode {
  const counts = new Map<LanguageCode, number>();
  let latin = 0;
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0;
    if ((cp >= 0x41 && cp <= 0x5a) || (cp >= 0x61 && cp <= 0x7a)) {
      latin++;
      continue;
    }
    for (const [lo, hi, lang] of SCRIPT_RANGES) {
      if (cp >= lo && cp <= hi) {
        counts.set(lang, (counts.get(lang) ?? 0) + 1);
        break;
      }
    }
  }
  let best: LanguageCode = 'en-IN';
  let bestCount = 0;
  for (const [lang, n] of counts) {
    if (n > bestCount) {
      best = lang;
      bestCount = n;
    }
  }
  // Code-mixed text ("मेरा notice period 30 days है") is normal and an Indic
  // voice reads the English words well; only mostly-Latin text goes to en-IN.
  if (bestCount === 0 || bestCount < (latin + bestCount) * 0.25) return 'en-IN';
  if (isLanguageCode(conversationLang) && SAME_SCRIPT[conversationLang] === best) {
    return conversationLang;
  }
  return best;
}

// Names a candidate may use for each language, in English and in the language itself.
const LANGUAGE_NAMES: Record<LanguageCode, string[]> = {
  'en-IN': ['english', 'angrezi', 'अंग्रेज़ी', 'अंग्रेजी'],
  'hi-IN': ['hindi', 'हिन्दी', 'हिंदी'],
  'ml-IN': ['malayalam', 'മലയാളം'],
  'ta-IN': ['tamil', 'தமிழ்'],
  'kn-IN': ['kannada', 'ಕನ್ನಡ'],
  'te-IN': ['telugu', 'తెలుగు'],
  'mr-IN': ['marathi', 'मराठी'],
  'bn-IN': ['bengali', 'bangla', 'বাংলা'],
  'gu-IN': ['gujarati', 'ગુજરાતી'],
  'pa-IN': ['punjabi', 'panjabi', 'ਪੰਜਾਬੀ'],
  'od-IN': ['odia', 'oriya', 'ଓଡ଼ିଆ'],
};

/** The supported languages named in free text such as "Malayalam, English and a bit of Hindi". */
export function parseLanguages(text: string | null | undefined): LanguageCode[] {
  const lower = (text ?? '').toLowerCase();
  return LANGUAGE_CODES.filter((code) => LANGUAGE_NAMES[code].some((name) => lower.includes(name)));
}

/**
 * The languages for the skills assessment's customer role-plays: always English (the roles are
 * customer-facing), then up to `extra` more that the candidate speaks, never their mother tongue.
 * Languages the job offers come first.
 */
export function rolePlayLanguages(
  spoken: string | null | undefined,
  motherTongue: string | null | undefined,
  jobLanguages: readonly LanguageCode[] = [],
  extra = 2,
): LanguageCode[] {
  const native = new Set(parseLanguages(motherTongue));
  const candidates = parseLanguages(spoken).filter((l) => l !== 'en-IN' && !native.has(l));
  const preferred = [...candidates.filter((l) => jobLanguages.includes(l)), ...candidates.filter((l) => !jobLanguages.includes(l))];
  return ['en-IN', ...preferred.slice(0, extra)];
}
